import { Badge, Box, ButtonBase } from '@mui/material'
import type { NavigationItem } from '../navigation.types'

export type BottomNavigationProps = {
  activeItemId?: string | null
  onItemSelect?: (id: string) => void
  items: NavigationItem[]
}

const navBackground = '#434a51'
const navBorder = 'rgba(255, 255, 255, 0.12)'
const navHover = 'rgba(255, 255, 255, 0.06)'
const navActive = 'rgba(0, 0, 0, 0.18)'
const navFocus = 'rgba(33, 152, 243, 0.35)'

export function BottomNavigation({ activeItemId = null, items, onItemSelect }: BottomNavigationProps) {
  return (
    <Box
      component="nav"
      aria-label="Bottom navigation"
      sx={{
        backgroundColor: navBackground,
        borderTop: `1px solid ${navBorder}`,
        color: '#f8f9fa',
        display: 'flex',
        justifyContent: 'space-around',
        minHeight: 'calc(48px + env(safe-area-inset-bottom, 0px))',
        pb: 'env(safe-area-inset-bottom, 0px)',
        width: '100%',
      }}
    >
      {items.map(item => {
        const selected = item.id === activeItemId

        return (
          <ButtonBase
            key={item.id}
            aria-current={selected ? 'page' : undefined}
            aria-label={item.ariaLabel ?? item.label}
            disabled={item.disabled}
            onClick={() => onItemSelect?.(item.id)}
            sx={{
              alignItems: 'center',
              borderRadius: 0,
              borderTop: '2px solid transparent',
              color: item.disabled ? 'rgba(255, 255, 255, 0.3)' : '#f8f9fa',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              gap: '0.125rem',
              justifyContent: 'center',
              minHeight: 48,
              minWidth: 0,
              py: 0,
              transition: 'background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',
              '&:hover': {
                backgroundColor: item.disabled ? 'transparent' : navHover,
              },
              '&:focus-visible': {
                boxShadow: `inset 0 0 0 3px ${navFocus}`,
                outline: 'none',
              },
              ...(selected && {
                backgroundColor: navActive,
                borderTopColor: 'primary.main',
                color: '#ffffff',
              }),
            }}
          >
            {item.badgeContent ? (
              <Badge badgeContent={item.badgeContent} color="error">
                <Box aria-hidden="true" sx={{ display: 'flex', fontSize: 20, lineHeight: 1 }}>
                  {item.icon}
                </Box>
              </Badge>
            ) : (
              <Box aria-hidden="true" sx={{ display: 'flex', fontSize: 20, lineHeight: 1 }}>
                {item.icon}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: selected ? 600 : 500,
                lineHeight: 1.1,
              }}
            >
              {item.label}
            </Box>
          </ButtonBase>
        )
      })}
    </Box>
  )
}

export type BottomNavigationBarProps = BottomNavigationProps
export const BottomNavigationBar = BottomNavigation
